/**
 * InvestigationAnalyticsPage — tenant-admin view of Investigate Why
 * usage: volumes, status breakdown, module quality, weekly time series,
 * top askers, and actionability signal from verdicts. Data comes from
 * GET /api/investigate/analytics/fleet.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip as RechartTip, CartesianGrid,
  LineChart, Line,
} from 'recharts';
import {
  Loader2, Activity, CheckCircle2, XCircle, Clock, Users, Layers, Sparkles, TrendingUp, Bell, Send, PlayCircle,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { api } from '@/lib/api';

const STATUS_TINT = {
  completed: 'text-emerald-400 border-emerald-500/25 bg-emerald-500/10',
  failed: 'text-rose-400 border-rose-500/25 bg-rose-500/10',
  cancelled: 'text-muted-foreground border-border/40 bg-muted/20',
  awaiting_input: 'text-amber-300 border-amber-500/25 bg-amber-500/10',
  running: 'text-sky-300 border-sky-500/25 bg-sky-500/10',
  planning: 'text-sky-300 border-sky-500/25 bg-sky-500/10',
  synthesising: 'text-indigo-300 border-indigo-500/25 bg-indigo-500/10',
  queued: 'text-muted-foreground border-border/40 bg-muted/20',
};

function KpiCard({ label, value, sub, icon: Icon, testId }) {
  return (
    <div className="rounded-xl border border-border/50 bg-card/60 px-4 py-3" data-testid={testId}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-muted-foreground">
        {Icon && <Icon className="w-3 h-3" />}
        {label}
      </div>
      <p className="text-2xl font-semibold text-foreground font-mono mt-1">{value}</p>
      {sub && <p className="text-[11px] text-muted-foreground mt-0.5">{sub}</p>}
    </div>
  );
}

function formatSeconds(s) {
  if (s == null) return '—';
  if (s < 60) return `${s.toFixed(1)}s`;
  const m = Math.floor(s / 60);
  const r = Math.round(s - m * 60);
  return `${m}m ${r}s`;
}

export default function InvestigationAnalyticsPage() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [slackCfg, setSlackCfg] = useState(null);
  const [slackDraft, setSlackDraft] = useState({ enabled: false, webhook_url: '', min_confidence: 0.7 });
  const [slackSaving, setSlackSaving] = useState(false);
  const [slackTesting, setSlackTesting] = useState(false);
  const [deliveries, setDeliveries] = useState([]);

  const refresh = async (d = days) => {
    setLoading(true);
    try {
      const res = await api.fleetInvestigationAnalytics(d);
      setData(res);
    } catch (err) {
      toast.error(err.message || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const refreshSlack = async () => {
    try {
      const cfg = await api.getSlackAlerts();
      setSlackCfg(cfg);
      setSlackDraft({
        enabled: !!cfg.enabled,
        webhook_url: '',
        min_confidence: typeof cfg.min_confidence === 'number' ? cfg.min_confidence : 0.7,
      });
      const dv = await api.listSlackAlertDeliveries(10);
      setDeliveries(dv.deliveries || []);
    } catch (err) {
      // silently ignore — non-admins get 403 which is fine
    }
  };

  useEffect(() => { refresh(days); }, [days]);
  useEffect(() => { refreshSlack(); }, []);

  // Portfolio insights — recurring themes across recent verdicts.
  const [portfolio, setPortfolio] = useState(null);
  useEffect(() => {
    api.investigationPortfolioInsights(50)
      .then(setPortfolio)
      .catch(() => setPortfolio({ themes: [] }));
  }, []);

  // Weekly Portfolio Digest config
  const [digestCfg, setDigestCfg] = useState(null);
  const [digestDraft, setDigestDraft] = useState({ enabled: false, include_all_admins: true, extra_recipients: '' });
  const [digestSaving, setDigestSaving] = useState(false);
  const [digestTesting, setDigestTesting] = useState(false);
  const refreshDigest = async () => {
    try {
      const cfg = await api.getInvestigationDigestConfig();
      setDigestCfg(cfg);
      setDigestDraft({
        enabled: !!cfg.enabled,
        include_all_admins: cfg.include_all_admins !== false,
        extra_recipients: (cfg.extra_recipients || []).join(', '),
      });
    } catch (err) { /* keep silent — non-admin get 200 with empty defaults */ }
  };
  useEffect(() => { refreshDigest(); }, []);

  const saveDigest = async () => {
    setDigestSaving(true);
    try {
      const extras = (digestDraft.extra_recipients || '')
        .split(/[,\s;]+/).map(s => s.trim()).filter(Boolean);
      const cfg = await api.updateInvestigationDigestConfig({
        enabled: !!digestDraft.enabled,
        include_all_admins: !!digestDraft.include_all_admins,
        extra_recipients: extras,
      });
      setDigestCfg(cfg);
      toast.success('Digest preferences saved');
    } catch (err) {
      toast.error(err.message || 'Save failed');
    } finally { setDigestSaving(false); }
  };

  const testDigest = async () => {
    setDigestTesting(true);
    try {
      const res = await api.sendInvestigationDigestTest();
      if (res.sent > 0) toast.success(`Test digest sent to ${res.sent} recipient${res.sent === 1 ? '' : 's'}`);
      else toast.error(res.skipped_reason || 'No recipients — configure email + recipients first');
      refreshDigest();
    } catch (err) {
      toast.error(err.message || 'Send failed');
    } finally { setDigestTesting(false); }
  };

  const saveSlack = async () => {
    setSlackSaving(true);
    try {
      const payload = { enabled: slackDraft.enabled, min_confidence: slackDraft.min_confidence };
      // Only include webhook_url when the user typed a new one — the
      // backend stores the URL but only returns a redacted preview.
      if (slackDraft.webhook_url && slackDraft.webhook_url.trim()) {
        payload.webhook_url = slackDraft.webhook_url.trim();
      }
      const cfg = await api.putSlackAlerts(payload);
      setSlackCfg(cfg);
      setSlackDraft((s) => ({ ...s, webhook_url: '' }));
      toast.success('Slack alerts saved');
    } catch (err) {
      toast.error(err.message || 'Save failed');
    } finally {
      setSlackSaving(false);
    }
  };

  const testSlack = async () => {
    setSlackTesting(true);
    try {
      const res = await api.testSlackAlerts();
      if (res.ok) toast.success('Test message sent — check your Slack channel');
      else toast.error(res.error || `Slack returned ${res.status}`);
      refreshSlack();
    } catch (err) {
      toast.error(err.message || 'Test failed');
    } finally {
      setSlackTesting(false);
    }
  };

  const weekly = useMemo(() => {
    if (!data?.weekly) return [];
    return data.weekly.map((w) => ({ ...w, incomplete: Math.max(0, w.count - w.completed - w.failed) }));
  }, [data]);

  if (loading && !data) {
    return (
      <div className="p-6" data-testid="investigation-analytics-loading">
        <Loader2 className="w-5 h-5 text-primary animate-spin" />
      </div>
    );
  }
  if (!data) return null;

  const t = data.totals || {};
  const a = data.actionability || {};

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6" data-testid="investigation-analytics-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-heading font-bold text-foreground flex items-center gap-2">
            <Activity className="w-6 h-6 text-indigo-400" />
            Investigation Analytics
          </h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            How your team is using Investigate Why — volumes, module quality, and how often verdicts translate into follow-ups.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={String(days)} onValueChange={(v) => setDays(parseInt(v, 10))}>
            <SelectTrigger className="h-9 w-36" data-testid="investigation-analytics-window">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
              <SelectItem value="180">Last 180 days</SelectItem>
              <SelectItem value="365">Last year</SelectItem>
            </SelectContent>
          </Select>
          <Link to="/admin/ai-agents">
            <Button variant="outline" size="sm" className="h-9" data-testid="investigation-analytics-agents-link">
              <Sparkles className="w-3.5 h-3.5 mr-1.5" /> AI Agents
            </Button>
          </Link>
          <Link to="/investigations">
            <Button variant="outline" size="sm" className="h-9" data-testid="investigation-analytics-history-link">History</Button>
          </Link>
        </div>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="investigation-analytics-kpis">
        <KpiCard label="Investigations" value={t.total ?? 0} icon={Activity} testId="kpi-total" />
        <KpiCard label="Completed" value={t.completed ?? 0} sub={t.total ? `${((t.completed / t.total) * 100).toFixed(0)}% completion` : '—'} icon={CheckCircle2} testId="kpi-completed" />
        <KpiCard label="Avg duration" value={formatSeconds(t.avg_duration_seconds)} icon={Clock} testId="kpi-avg-duration" />
        <KpiCard label="Actionable verdicts" value={a.verdicts_with_followups ?? 0} sub={t.completed ? `${((a.actionability_rate || 0) * 100).toFixed(0)}% of completed` : '—'} icon={TrendingUp} testId="kpi-actionable" />
      </div>

      {/* Slack alerts config */}
      {slackCfg && (
        <div className="rounded-xl border border-border/40 bg-card/30 p-4" data-testid="slack-alerts-panel">
          <div className="flex items-center justify-between gap-2 mb-3">
            <div className="flex items-center gap-1.5 text-xs font-semibold text-foreground">
              <Bell className="w-3.5 h-3.5 text-amber-400" /> Slack alerts on high-confidence verdicts
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[11px] text-muted-foreground">Enabled</span>
              <Switch
                checked={!!slackDraft.enabled}
                onCheckedChange={(v) => setSlackDraft((s) => ({ ...s, enabled: !!v }))}
                data-testid="slack-alerts-enabled-switch"
              />
            </div>
          </div>
          <p className="text-[11px] text-muted-foreground mb-3">
            Paste a <a className="underline" target="_blank" rel="noreferrer" href="https://api.slack.com/messaging/webhooks">Slack Incoming Webhook URL</a> for your channel.
            When any investigation completes and at least one top driver&apos;s confidence meets your threshold, an alert is posted.
          </p>
          <div className="grid md:grid-cols-3 gap-3 items-end">
            <div className="md:col-span-2">
              <label className="text-[10px] uppercase tracking-wider text-muted-foreground">Webhook URL</label>
              <Input
                type="password"
                placeholder={slackCfg.webhook_url_configured ? `Configured (…${slackCfg.webhook_url_preview?.replace('…', '') || ''}). Type to replace.` : 'https://hooks.slack.com/services/…'}
                value={slackDraft.webhook_url}
                onChange={(e) => setSlackDraft((s) => ({ ...s, webhook_url: e.target.value }))}
                className="h-9 text-xs"
                data-testid="slack-alerts-webhook-input"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-muted-foreground">Min confidence</label>
              <Input
                type="number" min={0} max={1} step={0.05}
                value={slackDraft.min_confidence}
                onChange={(e) => setSlackDraft((s) => ({ ...s, min_confidence: parseFloat(e.target.value || '0') }))}
                className="h-9 text-xs"
                data-testid="slack-alerts-threshold-input"
              />
            </div>
          </div>
          <div className="flex items-center gap-2 mt-3">
            <Button size="sm" onClick={saveSlack} disabled={slackSaving} className="h-8 text-xs" data-testid="slack-alerts-save">
              {slackSaving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3 mr-1" />} Save
            </Button>
            <Button
              size="sm" variant="outline"
              onClick={testSlack}
              disabled={slackTesting || !slackCfg.webhook_url_configured}
              className="h-8 text-xs"
              data-testid="slack-alerts-test"
            >
              {slackTesting ? <Loader2 className="w-3 h-3 animate-spin" /> : <PlayCircle className="w-3 h-3 mr-1" />} Send test
            </Button>
            {slackCfg.updated_at && (
              <span className="text-[10px] text-muted-foreground">Last saved {new Date(slackCfg.updated_at).toLocaleString()}</span>
            )}
          </div>
          {deliveries.length > 0 && (
            <div className="mt-4">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Recent deliveries</div>
              <div className="space-y-1" data-testid="slack-alerts-deliveries">
                {deliveries.slice(0, 5).map((d, i) => {
                  const ok = d.result?.ok;
                  return (
                    <div key={i} className="flex items-center gap-2 text-[11px]" data-testid={`slack-delivery-${i}`}>
                      <span className={`inline-block w-1.5 h-1.5 rounded-full ${ok ? 'bg-emerald-400' : 'bg-rose-400'}`} />
                      <span className="text-muted-foreground font-mono">{new Date(d.ts).toLocaleString()}</span>
                      <span className="text-muted-foreground">·</span>
                      <span className="text-foreground/80">conf {Math.round((d.confidence || 0) * 100)}% ≥ {Math.round((d.threshold || 0) * 100)}%</span>
                      <span className="text-muted-foreground">·</span>
                      <span className={ok ? 'text-emerald-300' : 'text-rose-300'}>
                        {ok ? 'delivered' : (d.result?.error || `HTTP ${d.result?.status || '—'}`)}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Status breakdown pills */}
      <div className="rounded-xl border border-border/40 bg-card/30 p-4" data-testid="investigation-analytics-status">
        <div className="flex items-center gap-1.5 text-xs font-semibold text-foreground mb-2">
          <XCircle className="w-3.5 h-3.5 text-muted-foreground" /> Status breakdown
        </div>
        <div className="flex flex-wrap gap-1.5">
          {Object.entries(data.by_status || {}).map(([k, v]) => (
            <Badge key={k} className={`text-[10px] border ${STATUS_TINT[k] || 'bg-muted text-muted-foreground'}`} data-testid={`status-badge-${k}`}>
              {k.replace('_', ' ')} · {v}
            </Badge>
          ))}
          {Object.keys(data.by_status || {}).length === 0 && (
            <span className="text-xs text-muted-foreground">No investigations in this window yet.</span>
          )}
        </div>
      </div>

      {/* Weekly bar chart */}
      <div className="rounded-xl border border-border/40 bg-card/30 p-4" data-testid="investigation-analytics-weekly">
        <div className="flex items-center gap-1.5 text-xs font-semibold text-foreground mb-3">
          <Clock className="w-3.5 h-3.5 text-muted-foreground" /> Weekly volume
        </div>
        {weekly.length === 0 ? (
          <p className="text-xs text-muted-foreground">Not enough data yet.</p>
        ) : (
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={weekly} margin={{ top: 4, right: 8, left: -8, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis dataKey="week" tick={{ fontSize: 10, fill: 'currentColor' }} />
                <YAxis tick={{ fontSize: 10, fill: 'currentColor' }} />
                <RechartTip contentStyle={{ background: '#0e0f13', border: '1px solid rgba(255,255,255,0.08)', fontSize: 11 }} />
                <Bar dataKey="completed" stackId="s" fill="#22d3a5" name="Completed" />
                <Bar dataKey="failed" stackId="s" fill="#f87171" name="Failed" />
                <Bar dataKey="incomplete" stackId="s" fill="#6b7280" name="Other" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Portfolio Insights — recurring themes across recent verdicts */}
      {portfolio && portfolio.themes && portfolio.themes.length > 0 && (
        <div className="rounded-xl border border-indigo-500/25 bg-indigo-500/5 p-4" data-testid="portfolio-insights">
          <div className="flex items-center gap-1.5 text-xs font-semibold text-foreground mb-2">
            <Sparkles className="w-3.5 h-3.5 text-indigo-400" /> Portfolio insights — recurring themes
            <span className="text-[10px] text-muted-foreground font-normal ml-1">across last {portfolio.window_investigations} verdicts</span>
          </div>
          <div className="flex flex-wrap gap-1.5" data-testid="portfolio-themes">
            {portfolio.themes.slice(0, 12).map((t) => (
              <div
                key={t.theme}
                className="group relative px-2 py-1 rounded-md text-[11px] border border-indigo-500/30 bg-indigo-500/10 text-indigo-200 cursor-help"
                data-testid={`portfolio-theme-${t.theme}`}
                title={`Appears in ${t.count} investigations (${Math.round(t.share_of_investigations * 100)}%). Examples: ${t.example_drivers.map((d) => d.statement).join(' · ')}`}
              >
                <span className="font-mono font-semibold">{t.theme}</span>
                <span className="ml-1.5 text-[10px] text-indigo-300/70">×{t.count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Weekly Portfolio Digest — email schedule + recipients */}
      <div className="rounded-xl border border-emerald-500/25 bg-emerald-500/5 p-4" data-testid="digest-config-card">
        <div className="flex items-center gap-1.5 text-xs font-semibold text-foreground mb-2">
          <Sparkles className="w-3.5 h-3.5 text-emerald-400" /> Weekly Portfolio Digest
          <span className="text-[10px] text-muted-foreground font-normal ml-1">Monday 08:00 UTC</span>
        </div>
        <p className="text-[11px] text-muted-foreground mb-3">
          Emails a summary of recurring investigation themes and module quality to admins each Monday.
          Uses your existing tenant email provider (Resend / SendGrid).
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-start">
          <label className="flex items-center gap-2 text-[11px] cursor-pointer">
            <input type="checkbox" checked={!!digestDraft.enabled}
              onChange={(e) => setDigestDraft(d => ({ ...d, enabled: e.target.checked }))}
              data-testid="digest-enabled" />
            <span>Enable weekly delivery</span>
          </label>
          <label className="flex items-center gap-2 text-[11px] cursor-pointer">
            <input type="checkbox" checked={!!digestDraft.include_all_admins}
              onChange={(e) => setDigestDraft(d => ({ ...d, include_all_admins: e.target.checked }))}
              data-testid="digest-include-admins" />
            <span>Include all tenant admins</span>
          </label>
          <div className="md:col-span-3">
            <label className="text-[10px] text-muted-foreground">Extra recipients (comma-separated)</label>
            <input type="text" value={digestDraft.extra_recipients}
              onChange={(e) => setDigestDraft(d => ({ ...d, extra_recipients: e.target.value }))}
              placeholder="ops-lead@acme.com, analytics@acme.com"
              className="w-full mt-1 px-2 py-1.5 rounded bg-muted/30 border border-border/30 text-[11px] font-mono"
              data-testid="digest-extra-recipients" />
          </div>
        </div>
        <div className="flex items-center gap-2 mt-3">
          <button onClick={saveDigest} disabled={digestSaving}
            className="px-3 py-1 rounded bg-emerald-500 text-white text-[11px] font-medium disabled:opacity-50"
            data-testid="digest-save">{digestSaving ? 'Saving…' : 'Save'}</button>
          <button onClick={testDigest} disabled={digestTesting}
            className="px-3 py-1 rounded border border-border/40 bg-muted/20 hover:bg-muted/30 text-[11px] font-medium disabled:opacity-50"
            data-testid="digest-send-test">{digestTesting ? 'Sending…' : 'Send test now'}</button>
          {digestCfg && digestCfg.last_sent_iso && (
            <span className="text-[10px] text-muted-foreground ml-1" data-testid="digest-last-sent">
              Last sent {new Date(digestCfg.last_sent_iso).toLocaleString()} to {digestCfg.last_send_count || 0} recipient(s)
            </span>
          )}
        </div>
      </div>

      {/* Module table */}
      <div className="rounded-xl border border-border/40 bg-card/30 p-4" data-testid="investigation-analytics-modules">
        <div className="flex items-center gap-1.5 text-xs font-semibold text-foreground mb-3">
          <Layers className="w-3.5 h-3.5 text-muted-foreground" /> Modules — usage &amp; quality
        </div>
        {(data.by_module || []).length === 0 ? (
          <p className="text-xs text-muted-foreground">No module runs recorded.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border/30">
                  <th className="text-left py-1.5 pr-2 font-medium">Module</th>
                  <th className="text-right py-1.5 px-2 font-medium">Runs</th>
                  <th className="text-right py-1.5 px-2 font-medium">Avg confidence</th>
                  <th className="text-right py-1.5 px-2 font-medium">Avg effect</th>
                  <th className="text-right py-1.5 px-2 font-medium">Evidence rate</th>
                  <th className="text-right py-1.5 px-2 font-medium w-24">Trend</th>
                </tr>
              </thead>
              <tbody>
                {data.by_module.map((m) => (
                  <tr key={m.module_key} className="border-b border-border/20" data-testid={`module-row-${m.module_key}`}>
                    <td className="py-1.5 pr-2 font-mono text-foreground/90">{m.module_key}</td>
                    <td className="text-right py-1.5 px-2 font-mono">{m.count}</td>
                    <td className="text-right py-1.5 px-2 font-mono">{m.avg_confidence == null ? '—' : (m.avg_confidence * 100).toFixed(0) + '%'}</td>
                    <td className="text-right py-1.5 px-2 font-mono">{m.avg_effect_size == null ? '—' : m.avg_effect_size.toFixed(2)}</td>
                    <td className="text-right py-1.5 px-2 font-mono">
                      <span className={m.evidence_rate < 0.5 ? 'text-rose-400' : m.evidence_rate < 0.8 ? 'text-amber-300' : 'text-emerald-400'}>
                        {(m.evidence_rate * 100).toFixed(0)}%
                      </span>
                    </td>
                    <td className="py-0 px-2 w-24" data-testid={`module-sparkline-${m.module_key}`}>
                      {(m.trend && m.trend.length >= 2) ? (
                        <div style={{ width: 80, height: 24, minWidth: 40, minHeight: 16 }}>
                          <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={m.trend} margin={{ top: 2, right: 2, left: 2, bottom: 2 }}>
                              <Line type="monotone" dataKey="count" stroke="#818cf8" strokeWidth={1.5} dot={false} isAnimationActive={false} />
                            </LineChart>
                          </ResponsiveContainer>
                        </div>
                      ) : (
                        <span className="text-[10px] text-muted-foreground">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Top askers */}
      <div className="rounded-xl border border-border/40 bg-card/30 p-4" data-testid="investigation-analytics-top-users">
        <div className="flex items-center gap-1.5 text-xs font-semibold text-foreground mb-3">
          <Users className="w-3.5 h-3.5 text-muted-foreground" /> Top question askers
        </div>
        {(data.top_users || []).length === 0 ? (
          <p className="text-xs text-muted-foreground">No investigations yet.</p>
        ) : (
          <div className="space-y-1">
            {data.top_users.map((u) => (
              <div key={u.user_id} className="flex items-center justify-between text-xs" data-testid={`top-user-${u.user_id}`}>
                <span className="text-foreground/90 truncate">{u.display || u.user_id}</span>
                <span className="text-muted-foreground font-mono">{u.count}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
